/* useCallChat — live text chat during a Scan2Call
   ------------------------------------------------
   Shared by both sides of the call (visitor page + resident's
   ConnectedCallView). Transport is simple 2s polling against
   GET /doorbell/ring/:id/chat — the visitor has no authenticated
   WebSocket, so polling keeps both sides on one uniform code path.
   Messages persist on the ring doc, so they survive into ring history.
*/
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const CHAT_POLL_MS = 2000;
export const CHAT_MAX_LEN = 500;

/**
 * @param {{ ringId: string|null, mySender: "visitor"|"resident", active: boolean }} opts
 */
export function useCallChat({ ringId, mySender, active }) {
  const [messages, setMessages] = useState([]);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const readCountRef = useRef(0);

  // New ring → clean slate (e.g. visitor taps "Call again").
  useEffect(() => {
    setMessages([]);
    setUnread(0);
    setOpen(false);
    readCountRef.current = 0;
  }, [ringId]);

  // Server array is canonical order; keep any optimistic local messages
  // the poll response hasn't caught up with yet.
  const mergeServer = useCallback((server) => {
    setMessages((prev) => {
      const ids = new Set(server.map((m) => m.id));
      return [...server, ...prev.filter((m) => !ids.has(m.id))];
    });
  }, []);

  useEffect(() => {
    if (!active || !ringId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/doorbell/ring/${ringId}/chat`);
        if (!cancelled) mergeServer(r.data?.messages || []);
      } catch { /* noop — next poll retries */ }
    };
    tick();
    const id = setInterval(tick, CHAT_POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [active, ringId, mergeServer]);

  // Unread badge — counts other-party messages that arrived while the
  // panel was closed; clears the moment it opens.
  useEffect(() => {
    if (open) {
      readCountRef.current = messages.length;
      setUnread(0);
      return;
    }
    setUnread(
      messages.slice(readCountRef.current).filter((m) => m.sender !== mySender).length
    );
  }, [messages, open, mySender]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || "").trim().slice(0, CHAT_MAX_LEN);
    if (!trimmed || !ringId) return false;
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/doorbell/ring/${ringId}/chat`,
        { text: trimmed },
        { withCredentials: true },
      );
      const msg = r.data?.message;
      if (msg) {
        setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      }
      return true;
    } catch {
      return false;
    } finally {
      setSending(false);
    }
  }, [ringId]);

  return { messages, unread, open, setOpen, sending, sendMessage };
}
